import type { VariantProps } from 'class-variance-authority'

import * as React from 'react'
import { cva } from 'class-variance-authority'
import { XIcon } from 'lucide-react'
import { Slot } from 'radix-ui'

import { cn } from '@/shared/lib/utils'

const tagVariants = cva(
	'inline-flex items-center justify-center rounded-full border border-solid text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none transition-[color,box-shadow] overflow-hidden',
	{
		variants: {
			interactive: {
				true: 'cursor-pointer select-none',
			},
			variant: {
				default: 'border bg-background',
				neutral:
					'border-slate-300 bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600',
				success:
					'border-green-300 bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400 dark:border-green-800',
				warning:
					'border-orange-300 bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400 dark:border-orange-800',
				destructive:
					'border-red-300 bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400 dark:border-red-800',
				info: 'border-sky-300 bg-sky-50 text-sky-600 dark:bg-sky-950 dark:text-sky-400 dark:border-sky-800',
				alert:
					'border-yellow-300 bg-yellow-50 text-yellow-600 dark:bg-yellow-950 dark:text-yellow-400 dark:border-yellow-800',
				epic: 'border-violet-300 bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400 dark:border-violet-800',
			},
			size: {
				default: 'h-6 min-w-6 px-2 py-1',
				sm: 'h-5 min-w-5 px-2 py-0.5 [&>svg]:size-3',
				icon: 'size-6 items-center justify-center p-0',
				'icon-sm': 'size-5 items-center justify-center p-0',
			},
		},

		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	}
)
type TagProps = React.ComponentProps<'span'> &
	VariantProps<typeof tagVariants> & { asChild?: boolean }
function Tag({
	className,
	variant,
	asChild = false,
	size,
	interactive,
	...props
}: TagProps) {
	const Comp = asChild ? Slot.Root : 'span'

	return (
		<Comp
			data-slot='tag'
			className={cn(tagVariants({ variant, size, interactive }), className)}
			{...props}
		/>
	)
}
const TagRemove = (props: React.ComponentProps<'button'>) => (
	<span
		{...props}
		className='inline-flex cursor-pointer items-center justify-center border-none bg-transparent text-foreground'
	>
		<XIcon className='size-4' />
	</span>
)
export { Tag, type TagProps, TagRemove, tagVariants }
